import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface ReportModalProps {
  targetType: 'thread' | 'comment'
  targetId: string
  onClose: () => void
  onSuccess: () => void
}

const REPORT_REASONS = [
  'Spam',
  'Harassment',
  'Inappropriate Content',
  'Misinformation',
  'Other',
] as const

export default function ReportModal({
  targetType,
  targetId,
  onClose,
  onSuccess,
}: ReportModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const reasonSelectRef = useRef<HTMLSelectElement>(null)

  // Focus first input on mount
  useEffect(() => {
    reasonSelectRef.current?.focus()
  }, [])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSubmitting])

  const createReport = useMutation({
    mutationFn: async (data: { reason: string }) => {
      if (!user) throw new Error('Not authenticated')

      // Check if user has already reported this content
      const { data: existingReport, error: checkError } = await supabase
        .from('reports')
        .select('id')
        .eq('target_type', targetType)
        .eq('target_id', targetId)
        .eq('reporter_id', user.id)
        .single()

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 is "not found" which is fine
        throw checkError
      }

      if (existingReport) {
        throw new Error('You have already reported this content')
      }

      // Create the report
      const { error: reportError } = await supabase
        .from('reports')
        .insert({
          target_type: targetType,
          target_id: targetId,
          reporter_id: user.id,
          reason: data.reason,
          status: 'pending',
        })

      if (reportError) throw reportError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      setReason('')
      onSuccess()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Basic validation
    if (!reason.trim()) {
      setError('Please select a reason for reporting')
      return
    }

    setIsSubmitting(true)
    try {
      await createReport.mutateAsync({
        reason: reason.trim(),
      })
    } catch (err: any) {
      setError(err.message || 'Failed to submit report')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-title"
    >
      <div
        className="rounded-lg p-6 max-w-md w-full"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2
            id="report-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Report {targetType === 'thread' ? 'Thread' : 'Comment'}
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
            disabled={isSubmitting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Reason Selection */}
          <div>
            <label
              htmlFor="report-reason"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Reason for Reporting
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <select
              ref={reasonSelectRef}
              id="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
              disabled={isSubmitting}
            >
              <option value="">Select a reason...</option>
              {REPORT_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div
              role="alert"
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

